<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Документы</div>

            <div class="content-header__background"></div>
        </div>

        <div class="content__body">
            <div class="documents-list__content">
                <div class="documents-list__list">
                    <div
                        v-for="document in arDocuments"
                        :key="'document-' + document.id"
                        class="document-item"
                    >
                        <div class="document-item__body">
                            <div class="document-item__title">{{ document.name }}</div>
                            <div class="document-item__info">
                                <div class="document-item__type">{{ getDocumentExtension(document)?.toUpperCase() }}</div>
                                <div class="document-item__size">{{ formatFileSize(document.size) }}</div>
                            </div>
                        </div>

                        <div class="document-item__actions">
                            <div
                                class="document-item__action"
                                @click="handleDownloadDocument(document)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/download.svg" />
                            </div>

                            <div
                                v-if="canDeleteDocument()"
                                class="document-item__action danger"
                                @click="handleDeleteDocument(document)"
                            >
                                <img v-svg-inline src="@/assets/icons/ui/trash.svg" />
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="canCreateDocument()"
                    class="documents-list__import"
                    @dragover.prevent
                    @drop.prevent="onDropFiles"
                >
                    <div class="documents-list__import-wrapper">
                        <div class="documents-list__import-title">
                            Перетащите сюда или Выберите файлы
                        </div>

                        <UISimpleButton
                            button-type="outlined"
                            class="documents-list__import-button"
                            :loading="uploadLoading"
                            @clicked="handleOpenFileDialog"
                        >
                            Выбрать файл
                        </UISimpleButton>
                    </div>

                    <input
                        ref="fileInput"
                        type="file"
                        multiple
                        :accept="acceptedFiles.join(', ')"
                        style="display: none;"
                        @change="handleFileInput"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { h, inject, onMounted, ref } from 'vue';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import {
        Document,
        DocumentCreateForm,
        DocumentCreateRequestResult,
    } from '@/types/document';

    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useDocumentRepository from '@/repositories/document/useDocumentRepository.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import { choice, formatFileSize } from '@/utils/NumberFormatter.ts';

    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import DocumentConfirmDelete from '@/components/documents/DocumentConfirmDelete/DocumentConfirmDelete.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const { arDocuments, createDocuments, getDocuments, deleteDocument } = useDocumentRepository();
    const { downloadDocument } = useDocumentDownloader();
    const { canCreateDocument, canDeleteDocument } = usePermission();
    const { showError, showSuccess } = useNotifications();

    const fileInput = ref<HTMLInputElement | null>(null);
    const uploadLoading = ref<boolean>(false);

    const acceptedFiles: string[] = ['application/pdf'];

    function handleDownloadDocument(document: Document): void {
        downloadDocument(document.id, document.original_file_name);
    }

    function handleCreateDocuments(files: File[]): void {
        uploadLoading.value = true;
        const acceptFileErrors: string[] = [];

        const forms: DocumentCreateForm[] = files
            .filter((file: File) => {
                if (!acceptFile(file)) {
                    acceptFileErrors.push(`Неподдерживаемый формат файла ${file.name}`);

                    return false;
                }

                return true;
            })
            .map((file: File) => ({
                name: file.name.replace(/\.[^.]+$/g, ''),
                file: file,
            }));

        if (acceptFileErrors.length > 0) {
            showError({
                errors: acceptFileErrors,
            });
        }

        if (forms.length > 0) {
            createDocuments(forms)
                .then((results: DocumentCreateRequestResult[]) => {
                    const uploadedCount = results
                        .filter((result: DocumentCreateRequestResult) => result.errors === undefined)
                        .length;

                    if (uploadedCount > 0) {
                        showSuccess({
                            title: choice(uploadedCount, 'Загружен', 'Загружено', 'Загружено')
                                + ' ' + uploadedCount + ' '
                                + choice(uploadedCount, 'документ', 'документа', 'документов'),
                        });
                    }

                    fetchDocuments();
                })
                .finally(() => {
                    uploadLoading.value = false;
                });
        }
        else {
            uploadLoading.value = false;
        }
    }

    function handleDeleteDocument(document: Document): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(DocumentConfirmDelete, { document: document }),
            },
            props: {
                onConfirm: async () => {
                    await deleteDocument(document.id);
                    fetchDocuments();
                },
            },
        });
    }

    function onDropFiles(event: DragEvent): void {
        if (uploadLoading.value || !event.dataTransfer?.files?.length) {
            return;
        }

        handleCreateDocuments(Array.from(event.dataTransfer.files));
    }

    function handleOpenFileDialog(): void {
        fileInput.value?.click();
    }

    function handleFileInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }

        handleCreateDocuments(Array.from(input.files));
        input.value = '';
    }

    function getDocumentExtension(document: Document): string | undefined {
        return document.mime_type.split('/')[1];
    }

    function acceptFile(file: File): boolean {
        return !acceptedFiles.length || acceptedFiles.includes(file.type);
    }

    function fetchDocuments(): void {
        getDocuments();
    }

    onMounted(() => {
        fetchDocuments();
    });
</script>

<style scoped lang="scss">
    @use './DocumentsList.scss' as *;
</style>
